// Shared server-time singleton.
// Uses ActionCable TimerChannel + performance.now() (monotonic clock) so the
// current time is immune to system-clock / timezone changes on the client.
//
// IMPORTANT: Local browser time (new Date()) is NEVER used. If the server time
// has not been seeded or synced, now() returns null and callers must skip.

import { createConsumer } from "@rails/actioncable"

let consumer = null
let subscription = null
let serverTimeMs = null   // server UTC epoch-ms at last sync
let localAtSync = null    // performance.now() at last sync
let syncInterval = null
let refCount = 0
let currentUserId = null

function getConsumer() {
  if (!consumer) consumer = createConsumer()
  return consumer
}

function requestSync() {
  if (subscription && currentUserId) {
    subscription.send({ user_id: currentUserId })
  }
}

// Seed the server time from a server-rendered epoch-ms value.
// Only takes effect if ActionCable hasn't synced yet (preserves more accurate
// live sync). Call this in every Stimulus controller connect() that uses now().
function seed(epochMs) {
  if (serverTimeMs === null && typeof epochMs === "number" && epochMs > 0) {
    serverTimeMs = epochMs
    localAtSync = performance.now()
  }
}

// Call from controller connect(). First caller creates the subscription.
function subscribe(userId) {
  refCount++
  if (subscription) return

  currentUserId = userId
  subscription = getConsumer().subscriptions.create(
    { channel: "TimerChannel", user_id: userId },
    {
      connected() { requestSync() },
      received(data) {
        serverTimeMs = new Date(data.server_time).getTime()
        localAtSync = performance.now()
      }
    }
  )
  syncInterval = setInterval(requestSync, 30000)
}

// Call from controller disconnect().
function unsubscribe() {
  refCount--
  if (refCount <= 0) {
    if (subscription) { subscription.unsubscribe(); subscription = null }
    if (syncInterval) { clearInterval(syncInterval); syncInterval = null }
    refCount = 0
    currentUserId = null
  }
}

// Returns a Date representing the current server time, unaffected by
// client clock or timezone changes. Returns null if not yet seeded/synced —
// callers MUST handle null (skip the tick, keep server-rendered text visible).
function now() {
  if (serverTimeMs !== null && localAtSync !== null) {
    return new Date(serverTimeMs + (performance.now() - localAtSync))
  }
  return null
}

export { seed, subscribe, unsubscribe, now, requestSync }
